import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchEpics } from '../../lib/api/epics';
import type { EpicSummary } from '../../../../shared/types/epics';
import { UrlTabs } from '../ui/UrlTabs';
import { EpicBoard } from './EpicBoard';
import { EpicTable } from './EpicTable';
import { placementForState, projectColor, shortRepo } from './boardLanes';

const PAGE_LIMIT = 100; // backend max per request
const POLL_INTERVAL_MS = 15_000;

type View = 'board' | 'backlog';
type KindFilter = 'all' | 'epic' | 'bugfix';

/**
 * Workspace epic board.
 *
 * One board for the whole workspace: epics and bug fixes from every project/repo
 * share the swimlanes (see boardLanes.ts for the state → lane mapping). Each card
 * carries a colour-coded project chip so mixed-project work stays legible.
 *
 * Tabs: Board (in flight + done) | Backlog (defined but not picked up yet).
 * URL state (?view= &kind= &repo= &archived=1) so a shared link reproduces the view.
 */
export const EpicDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view: View = searchParams.get('view') === 'backlog' ? 'backlog' : 'board';
  const kindParam = searchParams.get('kind');
  const kind: KindFilter = kindParam === 'epic' || kindParam === 'bugfix' ? kindParam : 'all';
  const repo = searchParams.get('repo') ?? undefined;
  const showArchived = searchParams.get('archived') === '1';

  const setParam = (key: string, value?: string) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  // One fetch for the whole workspace; kind/repo filtering is client-side so the
  // repo dropdown and tab counts stay stable while filtering.
  const { data, isLoading, error } = useQuery({
    queryKey: ['epics-board', showArchived],
    queryFn: async () => {
      const [epicResp, bugResp] = await Promise.all([
        fetchEpics({ kind: 'epic', limit: PAGE_LIMIT, includeArchived: showArchived }),
        fetchEpics({ kind: 'bugfix', limit: PAGE_LIMIT, includeArchived: showArchived }),
      ]);
      return {
        // De-dupe by epicRef (numbers repeat across repos, refs don't).
        epics: Array.from(
          new Map([...epicResp.data.epics, ...bugResp.data.epics].map((e) => [e.epicRef, e])).values(),
        ),
        truncated: Boolean(epicResp.data.nextCursor || bugResp.data.nextCursor),
      };
    },
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const all = data?.epics ?? [];

  const repoOptions = useMemo(() => Array.from(new Set(all.map((e) => e.repo))).sort(), [all]);

  const filtered = useMemo(
    () =>
      all.filter(
        (e) => (kind === 'all' || e.kind === kind) && (!repo || e.repo === repo || (e.involvedRepos?.includes(repo) ?? false)),
      ),
    [all, kind, repo],
  );

  const { boardItems, backlogItems } = useMemo(() => {
    const board: EpicSummary[] = [];
    const backlog: EpicSummary[] = [];
    for (const e of filtered) (placementForState(e.state) === 'backlog' ? backlog : board).push(e);
    backlog.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { boardItems: board, backlogItems: backlog };
  }, [filtered]);

  const handleOpen = (epicRef: string) => navigate(`/epics/${encodeURIComponent(epicRef)}`);

  return (
    <div className="space-y-4">
      <UrlTabs
        param="view"
        defaultId="board"
        testIdPrefix="tab"
        tabs={[
          { id: 'board', label: 'Board', badge: isLoading ? undefined : boardItems.length },
          { id: 'backlog', label: 'Backlog', badge: isLoading ? undefined : backlogItems.length },
        ]}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div role="group" aria-label="Kind" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            {(
              [
                ['all', 'All'],
                ['epic', '📦 Epics'],
                ['bugfix', '🔧 Bugs'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                data-testid={`kind-${id}`}
                aria-pressed={kind === id}
                onClick={() => setParam('kind', id === 'all' ? undefined : id)}
                className={
                  'px-2.5 py-1 text-xs rounded-md transition-colors ' +
                  (kind === id ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-slate-600 hover:text-slate-900')
                }
              >
                {label}
              </button>
            ))}
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <span className="sr-only">Project</span>
            <select
              aria-label="repo"
              id="epic-repo-filter"
              value={repo ?? ''}
              disabled={isLoading}
              onChange={(e) => setParam('repo', e.target.value || undefined)}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
            >
              <option value="">All projects</option>
              {repoOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>

          {/* Legend: which colour is which project (only the ones on screen). */}
          {repoOptions.length > 1 && !repo && (
            <div data-testid="project-legend" className="hidden md:flex items-center gap-1.5 flex-wrap">
              {repoOptions.slice(0, 8).map((r) => (
                <button
                  key={r}
                  type="button"
                  title={`Show only ${r}`}
                  onClick={() => setParam('repo', r)}
                  className={`text-2xs rounded border px-1.5 py-0.5 ${projectColor(r).chip}`}
                >
                  {shortRepo(r)}
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="inline-flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
          <input
            type="checkbox"
            data-testid="show-archived-toggle"
            checked={showArchived}
            onChange={() => setParam('archived', showArchived ? undefined : '1')}
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-300"
          />
          Show archived
        </label>
      </div>

      {data?.truncated && (
        <p className="text-2xs text-slate-500">Showing the 100 most recent epics and 100 most recent bugs.</p>
      )}

      {view === 'board' ? (
        <EpicBoard epics={boardItems} isLoading={isLoading} error={error as Error | null} onCardClick={handleOpen} />
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            Defined but not picked up by Forge yet. They move onto the board once work starts.
          </p>
          <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
            <EpicTable
              epics={backlogItems}
              isLoading={isLoading}
              error={error as Error | null}
              onRowClick={handleOpen}
              showArchived={showArchived}
            />
          </div>
        </div>
      )}
    </div>
  );
};
